/**
=========================================================
* Trust Cargo - arrivals board
=========================================================

* The screen for the office wall: every container and every order with where
* it has got to, read the way an airport departures board is read.
*
* Built for a display nobody touches, so it looks after itself - it pages
* through rows on its own, swaps between the container and order boards,
* reloads the data on a timer, and never shows a dialog or an error box that
* would sit there until someone dismissed it.
*
* Public on purpose: a wall screen cannot log in.
*/

import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

const API = "https://trust-cargo.co/anas_backend/api";

const C = {
  navy: "#060F22",
  panel: "#0B1B3A",
  gold: "#C9A227",
  goldLight: "#E4C766",
  ivory: "#F7F4EE",
  dim: "rgba(247,244,238,0.55)",
  line: "rgba(201,162,39,0.22)",
};

/** How long a page of rows stays up, and how often the data is refetched. */
const PAGE_MS = 9000;
const REFRESH_MS = 120000;
const ROWS_PER_PAGE = 9;

/**
 * Every status the two systems produce, mapped onto the handful an airport
 * board uses. Anything unrecognised falls through to a neutral look rather
 * than disappearing - an unknown status still has to be readable.
 */
const TONES = [
  {
    match: /deliver|arrived|received|complete|done/i,
    label: null,
    color: "#2FBF71",
    glow: "rgba(47,191,113,0.35)",
  },
  { match: /ready|warehouse/i, label: null, color: "#4FA3F7", glow: "rgba(79,163,247,0.35)" },
  {
    match: /transit|shipped|sail|sea|way/i,
    label: null,
    color: "#E4C766",
    glow: "rgba(228,199,102,0.35)",
  },
  { match: /load|pack|prepar/i, label: null, color: "#C9A227", glow: "rgba(201,162,39,0.35)" },
  {
    match: /cancel|delay|hold|stuck|problem/i,
    label: null,
    color: "#E5484D",
    glow: "rgba(229,72,77,0.35)",
  },
];

const toneFor = (status) => {
  const found = TONES.find((t) => t.match.test(String(status || "")));
  return found || { color: C.ivory, glow: "rgba(247,244,238,0.2)" };
};

/** "(416)20/12/2024" -> "416", the number the office calls the shipment. */
const shipmentNo = (name) => {
  const m = String(name || "").match(/\((\d+)\)/);
  return m ? m[1] : String(name || "").slice(0, 8);
};

const dmy = (iso) => {
  if (!iso) return "--/--/----";
  const [y, m, d] = String(iso).split("-");
  return y && m && d ? `${d}/${m}/${y}` : String(iso);
};

/**
 * One cell of board type. `flip` restarts the drop animation whenever the
 * page changes, which is what gives the split-flap feel without wiring up a
 * real flap for every character.
 */
function Cell({ children, width, bold, color, size, flip }) {
  return (
    <SuiBox
      sx={{
        flex: width,
        minWidth: 0,
        px: 1,
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        fontFamily: "'Tajawal', sans-serif",
        fontVariantNumeric: "tabular-nums",
        letterSpacing: "0.04em",
        fontWeight: bold ? 800 : 600,
        fontSize: size || { xs: 15, md: 22, xl: 26 },
        color: color || C.ivory,
        animation: `tcFlap 420ms ease ${flip}ms both`,
        "@keyframes tcFlap": {
          from: { opacity: 0, transform: "translateY(-40%) rotateX(45deg)" },
          to: { opacity: 1, transform: "none" },
        },
      }}
    >
      {children}
    </SuiBox>
  );
}

Cell.defaultProps = { children: null, width: 1, bold: false, color: null, size: null, flip: 0 };
Cell.propTypes = {
  children: PropTypes.node,
  width: PropTypes.number,
  bold: PropTypes.bool,
  color: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  flip: PropTypes.number,
};

/** The status pill, glowing in its own colour. */
function Status({ value, flip }) {
  const tone = toneFor(value);

  return (
    <SuiBox sx={{ flex: 1.2, minWidth: 0, px: 1 }}>
      <SuiBox
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          px: { xs: 1.25, md: 2 },
          py: { xs: 0.4, md: 0.7 },
          borderRadius: 999,
          maxWidth: "100%",
          border: `1px solid ${tone.color}`,
          backgroundColor: `${tone.color}1A`,
          boxShadow: `0 0 18px ${tone.glow}`,
          animation: `tcFlap 420ms ease ${flip}ms both`,
        }}
      >
        <SuiBox
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            flexShrink: 0,
            backgroundColor: tone.color,
            animation: "tcPulse 1.8s ease-in-out infinite",
            "@keyframes tcPulse": {
              "0%,100%": { opacity: 1 },
              "50%": { opacity: 0.3 },
            },
          }}
        />
        <SuiTypography
          sx={{
            fontSize: { xs: 12, md: 17 },
            fontWeight: 800,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: tone.color,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value || "unknown"}
        </SuiTypography>
      </SuiBox>
    </SuiBox>
  );
}

Status.defaultProps = { value: "", flip: 0 };
Status.propTypes = { value: PropTypes.string, flip: PropTypes.number };

function Board() {
  const [containers, setContainers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [types, setTypes] = useState([]);
  const [view, setView] = useState("containers");
  const [page, setPage] = useState(0);
  const [now, setNow] = useState(() => new Date());
  const [loaded, setLoaded] = useState(false);
  // {containers, orders} counts of what the office pinned, or null
  const [picked, setPicked] = useState(null);

  const wrap = useRef(null);

  /* ---------------------------------------------------------------- */
  /* data                                                              */
  /* ---------------------------------------------------------------- */

  const load = async () => {
    const get = (url) =>
      fetch(url, { cache: "no-store" })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);

    /**
     * One request: the server already knows which rows were picked for the
     * board and which to fall back to. The older endpoints are only used if
     * that one is not there.
     */
    const [board, t] = await Promise.all([
      get(`${API}/v2/board`),
      get(`${API}/v2/container-types`),
    ]);

    if (Array.isArray(t)) setTypes(t);

    if (board && Array.isArray(board.containers)) {
      setContainers(board.containers);
      setOrders(Array.isArray(board.orders) ? board.orders : []);
      setPicked(board.picked || null);
      setLoaded(true);
      return;
    }

    const [c, o] = await Promise.all([
      get(`${API}/v2/typed-containers`),
      get(`${API}/v2/orders?limit=500`),
    ]);

    if (Array.isArray(c)) {
      setContainers(c);
    } else {
      const legacy = await get(`${API}/containers`);
      setContainers(legacy?.containers || []);
    }

    if (Array.isArray(o)) setOrders(o);
    setLoaded(true);
  };

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  // the clock, which is half the reason anyone looks at a board
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  /* ---------------------------------------------------------------- */
  /* what is on screen                                                 */
  /* ---------------------------------------------------------------- */

  const typeName = (id) => types.find((t) => String(t.id) === String(id))?.name;

  const rows = useMemo(() => {
    if (view === "containers") {
      /**
       * Sailing order: anything still moving first, then what has landed.
       * A board is read top-down for "what is coming", not for history.
       */
      const live = containers.filter((c) => !/deliver/i.test(String(c.status || "")));
      const done = containers.filter((c) => /deliver/i.test(String(c.status || "")));

      const byEta = (a, b) =>
        String(a.estimated_arrival_date || "9999").localeCompare(
          String(b.estimated_arrival_date || "9999")
        );

      return [...live.sort(byEta), ...done.sort(byEta)].map((c) => ({
        key: `c${c.id}`,
        a: shipmentNo(c.name),
        b: c.number && c.number !== "0" && c.number !== "-" ? c.number : "—",
        c: typeName(c.container_type_id) || "—",
        d: dmy(c.loading_date),
        e: dmy(c.estimated_arrival_date),
        status: c.status,
      }));
    }

    return orders.map((o) => ({
      key: `o${o.id}`,
      a: o.shop_code || "—",
      b: o.customer || "—",
      c: o.container || "—",
      d: dmy(o.date),
      e: Number(o.balance) > 0 ? `${Number(o.balance).toLocaleString("en-US")}` : "settled",
      status: o.status,
    }));
  }, [view, containers, orders, types]);

  const pages = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));

  /**
   * Page through the rows, and when the last page has been shown, hand over
   * to the other board. One timer drives both so they can never fight.
   */
  useEffect(() => {
    const id = setInterval(() => {
      setPage((p) => {
        if (p + 1 < pages) return p + 1;
        setView((v) => (v === "containers" ? "orders" : "containers"));
        return 0;
      });
    }, PAGE_MS);

    return () => clearInterval(id);
  }, [pages]);

  // a board that is showing page 3 of a list that just shrank to 1 page
  // would otherwise sit on an empty screen until the next tick
  useEffect(() => {
    if (page >= pages) setPage(0);
  }, [pages, page]);

  const visible = rows.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

  const heads =
    view === "containers"
      ? ["SHIPMENT", "CONTAINER NO", "TYPE", "LOADED", "ARRIVING", "STATUS"]
      : ["ORDER", "CUSTOMER", "CONTAINER", "ORDERED", "BALANCE", "STATUS"];

  const widths = view === "containers" ? [1, 1.6, 1.2, 1.1, 1.1] : [1, 1.8, 1.1, 1.1, 1.1];

  const toggleFullscreen = () => {
    const el = wrap.current;
    if (!el) return;

    if (document.fullscreenElement) document.exitFullscreen();
    else if (el.requestFullscreen) el.requestFullscreen();
  };

  const clock = now.toLocaleTimeString("en-GB", { hour12: false });
  const date = now.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const arriving = containers.filter((c) => !/deliver/i.test(String(c.status || ""))).length;
  const outstanding = orders.filter((o) => Number(o.balance) > 0).length;

  return (
    <SuiBox
      ref={wrap}
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        px: { xs: 2, md: 5 },
        py: { xs: 2, md: 3 },
        background: `
          radial-gradient(1200px 500px at 12% -10%, rgba(201,162,39,0.13), transparent 60%),
          linear-gradient(170deg, ${C.panel} 0%, ${C.navy} 60%)
        `,
      }}
    >
      {/* ---------------- header ---------------- */}
      <SuiBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          pb: 2,
          borderBottom: `2px solid ${C.line}`,
        }}
      >
        <SuiBox
          sx={{
            width: 52,
            height: 52,
            borderRadius: 2.5,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            background: `linear-gradient(135deg, ${C.goldLight}, ${C.gold})`,
          }}
        >
          <Icon sx={{ color: C.panel, fontSize: "30px !important" }}>directions_boat</Icon>
        </SuiBox>

        <SuiBox>
          <SuiTypography
            sx={{ fontSize: { xs: 20, md: 30 }, fontWeight: 800, color: C.ivory, lineHeight: 1.1 }}
          >
            TRUST CARGO
          </SuiTypography>
          <SuiTypography
            sx={{
              fontSize: { xs: 11, md: 14 },
              fontWeight: 700,
              color: C.gold,
              letterSpacing: "0.18em",
            }}
          >
            {view === "containers" ? "SHIPMENT ARRIVALS" : "ORDER STATUS"}
          </SuiTypography>
        </SuiBox>

        <SuiBox sx={{ flex: "1 1 auto" }} />

        {/* which board, and how far through it we are */}
        <SuiBox sx={{ display: "flex", gap: 1 }}>
          {["containers", "orders"].map((v) => (
            <SuiBox
              key={v}
              component="button"
              type="button"
              onClick={() => {
                setView(v);
                setPage(0);
              }}
              sx={{
                cursor: "pointer",
                fontFamily: "inherit",
                fontWeight: 800,
                fontSize: 12,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                px: 2,
                py: 0.9,
                borderRadius: 2,
                color: view === v ? C.panel : C.dim,
                border: `1px solid ${C.line}`,
                background:
                  view === v ? `linear-gradient(135deg, ${C.goldLight}, ${C.gold})` : "transparent",
              }}
            >
              {v}
            </SuiBox>
          ))}

          <SuiBox
            component="button"
            type="button"
            onClick={toggleFullscreen}
            aria-label="Fullscreen"
            sx={{
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
              width: 40,
              borderRadius: 2,
              color: C.ivory,
              border: `1px solid ${C.line}`,
              background: "transparent",
            }}
          >
            <Icon>fullscreen</Icon>
          </SuiBox>
        </SuiBox>

        <SuiBox sx={{ textAlign: "end", minWidth: 190 }}>
          <SuiTypography
            sx={{
              fontSize: { xs: 26, md: 40 },
              fontWeight: 800,
              color: C.ivory,
              lineHeight: 1,
              fontVariantNumeric: "tabular-nums",
              letterSpacing: "0.06em",
            }}
          >
            {clock}
          </SuiTypography>
          <SuiTypography sx={{ fontSize: { xs: 11, md: 13 }, color: C.dim }}>{date}</SuiTypography>
        </SuiBox>
      </SuiBox>

      {/* ---------------- column heads ---------------- */}
      <SuiBox
        sx={{
          display: "flex",
          alignItems: "center",
          px: { xs: 0.5, md: 1 },
          py: 1.5,
          borderBottom: `1px solid ${C.line}`,
        }}
      >
        {heads.map((h, i) => (
          <SuiBox key={h} sx={{ flex: widths[i] || 1.2, minWidth: 0, px: 1 }}>
            <SuiTypography
              sx={{
                fontSize: { xs: 10, md: 13 },
                fontWeight: 800,
                letterSpacing: "0.18em",
                color: C.gold,
              }}
            >
              {h}
            </SuiTypography>
          </SuiBox>
        ))}
      </SuiBox>

      {/* ---------------- rows ---------------- */}
      <SuiBox sx={{ flex: "1 1 auto" }}>
        {!loaded && (
          <SuiTypography sx={{ color: C.dim, textAlign: "center", py: 8, fontSize: 20 }}>
            Loading board...
          </SuiTypography>
        )}

        {loaded && visible.length === 0 && (
          <SuiTypography sx={{ color: C.dim, textAlign: "center", py: 8, fontSize: 20 }}>
            Nothing to show
          </SuiTypography>
        )}

        {visible.map((r, i) => (
          <SuiBox
            key={r.key}
            sx={{
              display: "flex",
              alignItems: "center",
              px: { xs: 0.5, md: 1 },
              py: { xs: 1, md: 1.4 },
              borderBottom: "1px solid rgba(247,244,238,0.06)",
              backgroundColor: i % 2 ? "rgba(255,255,255,0.02)" : "transparent",
            }}
          >
            <Cell width={widths[0]} bold color={C.goldLight} flip={i * 55}>
              {r.a}
            </Cell>
            <Cell width={widths[1]} flip={i * 55 + 30}>
              {r.b}
            </Cell>
            <Cell width={widths[2]} color={C.dim} flip={i * 55 + 60}>
              {r.c}
            </Cell>
            <Cell width={widths[3]} color={C.dim} flip={i * 55 + 90}>
              {r.d}
            </Cell>
            <Cell width={widths[4]} bold flip={i * 55 + 120}>
              {r.e}
            </Cell>
            <Status value={r.status} flip={i * 55 + 150} />
          </SuiBox>
        ))}
      </SuiBox>

      {/* ---------------- footer ---------------- */}
      <SuiBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          pt: 2,
          borderTop: `2px solid ${C.line}`,
        }}
      >
        <SuiTypography sx={{ fontSize: { xs: 12, md: 15 }, color: C.dim }}>
          {`${arriving} shipment${arriving === 1 ? "" : "s"} on the way · ${outstanding} order${
            outstanding === 1 ? "" : "s"
          } with a balance`}
        </SuiTypography>

        {/* say so when nothing was picked, so "why is everything here" has
            an answer without anyone having to ask */}
        {picked && picked.containers === 0 && picked.orders === 0 && (
          <SuiTypography sx={{ fontSize: { xs: 11, md: 13 }, color: C.gold }}>
            showing everything - nothing pinned yet
          </SuiTypography>
        )}

        <SuiBox sx={{ flex: "1 1 auto" }} />

        <SuiTypography sx={{ fontSize: { xs: 12, md: 15 }, color: C.dim }}>
          {`Page ${page + 1} / ${pages}`}
        </SuiTypography>

        {/* the bar empties as the page runs out, so the room can see it coming */}
        <SuiBox
          sx={{
            width: { xs: 90, md: 180 },
            height: 5,
            borderRadius: 3,
            overflow: "hidden",
            backgroundColor: "rgba(247,244,238,0.12)",
          }}
        >
          <SuiBox
            key={`${view}-${page}`}
            sx={{
              height: "100%",
              background: `linear-gradient(90deg, ${C.gold}, ${C.goldLight})`,
              animation: `tcSweep ${PAGE_MS}ms linear both`,
              "@keyframes tcSweep": { from: { width: "100%" }, to: { width: "0%" } },
            }}
          />
        </SuiBox>
      </SuiBox>
    </SuiBox>
  );
}

export default Board;
